import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AutomationTestCommand } from '../model';
import { actions, useSelector } from '../store';
import { selectCall, selectEndpoint, selectLine } from '../store/reducers/janus';
import css from './AutomationTest.module.scss';
import Toggle from './Toggle';

const ipcRenderer: null | {
  send(channel: string, ...args: any[]): void;
  on(channel: string, listener: (event: Event, ...args: any[]) => void): void;
  removeListener(channel: string, listener: (...args: any[]) => void): void;
} = (window as any).ipcRenderer ?? null

const ChannelName = 'automation-test'

enum ActionType {
  Dial = 'Dial',
  WaitForAnswer = 'Wait For Answer',
  Answer = 'Answer',
  HangUp = 'Hang Up',
  Hold = 'Hold',
  Resume = 'Resume',
  Idle = 'Idle',
}

type ActionableWork = {
  Action: ActionType
  PerformWork: () => void
  DelayMs: number
} | null

function getRandomInt(max: number) {
  return Math.floor(Math.random() * Math.floor(max));
}

export default function AutomationTest() {
  const endpoint = useSelector(selectEndpoint)
  const line = useSelector(selectLine)
  const call = useSelector(selectCall)
  const dispatch = useDispatch()

  const [endpointOnline, setEndpointOnline] = useState(false)
  const [nextAction, setNextAction] = useState<ActionableWork>(null)
  const [nextActionRemainingTime, setNextActionRemainingTime] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const retryCount = useRef(0)
  const extension = useRef<string | null>(null)
  const pin = useRef<string | null>(null)

  const [allowableActions, setAllowableActions] = useState<Array<ActionType>>()
  const [autoDialNumber, setAutoDialNumber] = useState('')
  const [autoAnswer, setAutoAnswer] = useState(true)
  const [allowHoldResume, setAllowHoldResume] = useState(true)
  const [allowHangUp, setAllowHangUp] = useState(true)
  const [allowIdle, setAllowIdle] = useState(true)
  const [minDelayMs, setMinDelayMs] = useState(2000)
  const [maxDelayMs, setMaxDelayMs] = useState(8000)

  const scheduleNextAction = useCallback((action: ActionType, work: () => void | undefined, delayMs: number) => {
    if (nextAction == null) {
      console.log(`Scheduling Automation Work: ${action}`)
      const performWork = () => {
        console.log(`Performing Automation Work: ${action}`)
        work()
        setNextAction(null)
        setNextActionRemainingTime(0)
      }

      const workAction: ActionableWork = {
        Action: action,
        PerformWork: performWork,
        DelayMs: delayMs,
      }

      setNextActionRemainingTime(delayMs)
      setNextAction(workAction)
    }
  }, [nextAction])

  const configureAutoAnswerMode = useCallback(() => {
    setAutoDialNumber('')
    setAutoAnswer(true)
    setAllowHoldResume(false)
    setAllowHangUp(false)
    setAllowIdle(false)
    setMinDelayMs(0)
    setMaxDelayMs(0)
  }, [])

  useEffect(() => {
    // by default we configure to nzvoip02
    const queryParams = new URLSearchParams(window.location.search)
    if (queryParams == null) return

    extension.current = queryParams.get('extension')
    pin.current = queryParams.get('pin')

    const autoAnswerMode = queryParams.get('autoAnswerMode')

    if (queryParams.get('onIncomingCall')) {
      setAutoAnswer(false)
    }

    if (extension.current != null && pin.current != null) {
      dispatch(actions.connectEndpoint(extension.current, pin.current))
    }

    if (autoAnswerMode != null) {
      configureAutoAnswerMode()
      setIsRunning(true)
    }
  }, [configureAutoAnswerMode, dispatch])

  useEffect(() => {
    if (ipcRenderer === null) return

    const onAutomationTestCommandEvent = (_: Event, args: AutomationTestCommand) => {
      console.log(ChannelName, args)
      switch (args.Command) {
        case 'connect':
          dispatch(actions.configureEndpoint(args.Settings))
          dispatch(actions.connectEndpoint(args.Extension, args.Pin))
          args.Result = true
          ipcRenderer.send(`${ChannelName}-result`, args)
          break
        case 'disconnect':
          args.Result = false
          ipcRenderer.send(`${ChannelName}-result`, args)
          break
        case 'auto-answer-mode':
          configureAutoAnswerMode()
          setIsRunning(true)
          args.Result = true
          ipcRenderer.send(`${ChannelName}-result`, args)
          break
      }
    }

    ipcRenderer.on(ChannelName, onAutomationTestCommandEvent)

    return () => {
      ipcRenderer.removeListener(`{ChannelName}-result`, onAutomationTestCommandEvent)
    }
  }, [configureAutoAnswerMode, dispatch]);

  useEffect(() => {
    if (nextActionRemainingTime > 0) {
      // not exactly the most accurate, but good enough for the purpose.
      setTimeout(() => setNextActionRemainingTime(nextActionRemainingTime - 100), 100)
    } else if (nextAction != null) {
      nextAction.PerformWork()
    }
  }, [nextAction, nextActionRemainingTime])

  useEffect(() => {
    if (isRunning) return

    // no longer running
    if (nextAction != null) {
      console.log('clearing next action')
      if (call != null && call.canHangUp) {
        dispatch(actions.hangUp())
      }
      setNextAction(null)
    }
  }, [call, dispatch, isRunning, nextAction])

  useEffect(() => {
    const isOnline = line.status === 'registered' && endpoint.status === 'online'
    if (isOnline && retryCount.current > 0) {
      retryCount.current = 0
    }

    if (ipcRenderer != null && !endpointOnline && isOnline) {
      console.log('sending endpoint online')
      ipcRenderer.send('endpoint-online', true)
      setEndpointOnline(true)
    } else if (ipcRenderer != null && endpointOnline && (line.status !== 'registered' || endpoint.status !== 'online')) {
      console.log('sending endpoint offline')
      ipcRenderer.send('endpoint-online', false)
      setEndpointOnline(false)
    }

    if (!isRunning) return
    if (line.statusError === '403 Forbidden' && line.status === 'registering') {
      retryCount.current = 999
    }

    if (retryCount.current < 3 && endpoint.canConnect
      && extension.current != null && pin.current != null
      && (line.status !== 'registered' || endpoint.status !== 'online')) {
      console.log('Not connected, will try in 5s')
      retryCount.current += 1
      setTimeout(() => {
        console.log('Attempt to auto reconnect, retryCount', retryCount.current)
        if (endpoint.canConnect && (line.status !== 'registered' || endpoint.status !== 'online')) {
          dispatch(actions.connectEndpoint(extension.current!, pin.current!))
        } else {
          console.log('Endpoint says we cannot connect yet')
        }
      }, 5000)
    }
  }, [dispatch, endpoint.canConnect, endpoint.status, endpointOnline, isRunning, line.status, line.statusError])

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isRunning) return
      if (nextAction != null) return
      if (line.status !== 'registered' || endpoint.status === 'offline') return
      if (line.statusError != null || endpoint.statusError != null) return


      let allowableActions: Array<ActionType> = []

      if (allowIdle) {
        allowableActions.push(ActionType.Idle)
      }

      if (call != null && call.canDial && autoDialNumber.length > 0) {
        allowableActions.push(ActionType.Dial)
      }

      if (call.canAnswer && !call.answerRequested) {
        allowableActions.push(ActionType.Answer)
      }

      if (call.canHold && allowHoldResume) {
        allowableActions.push(ActionType.Hold)
      }

      if (call.canResume && allowHoldResume) {
        allowableActions.push(ActionType.Resume)
      }

      if (call.canHangUp && allowHangUp) {
        allowableActions.push(ActionType.HangUp)
      }

      if (allowableActions.length === 0) {
        setAllowableActions(allowableActions)
        return
      }

      const index = getRandomInt(allowableActions.length)
      const minDelay = Number.isNaN(minDelayMs) ? 0 : minDelayMs
      const maxDelay = Number.isNaN(maxDelayMs) ? 0 : maxDelayMs
      const delay = maxDelay < minDelay ? minDelay : minDelay + getRandomInt(maxDelay - minDelay)

      switch (allowableActions[index]) {
        case ActionType.Dial:
          scheduleNextAction(ActionType.Dial, () => {
            if (call != null && call.canDial && autoDialNumber.length > 0) {
              dispatch(actions.dial(autoDialNumber))
            }
          }, delay)
          break
        case ActionType.Answer:
          scheduleNextAction(ActionType.Answer, () => {
            if (call != null && call.canAnswer) {
              dispatch(actions.answer())
            }
          }, delay)
          break
        case ActionType.Hold:
          scheduleNextAction(allowableActions[index], () => {
            if (call.canHold) {
              dispatch(actions.hold())
            }
          }, delay)
          break
        case ActionType.Resume:
          scheduleNextAction(allowableActions[index], () => {
            if (call.canResume) {
              dispatch(actions.resume())
            }
          }, delay)
          break
        case ActionType.HangUp:
          scheduleNextAction(allowableActions[index], () => {
            if (call.canHangUp) {
              dispatch(actions.hangUp())
            }
          }, delay)
          break
        case ActionType.Idle:
          scheduleNextAction(allowableActions[index], () => { }, delay)
          break
      }
      setAllowableActions(allowableActions)

    }, 1000);
    return () => clearInterval(interval);
  }, [allowHangUp, allowHoldResume, allowIdle, autoDialNumber, call, dispatch, endpoint.status, endpoint.statusError, isRunning, line.status, line.statusError, maxDelayMs, minDelayMs, nextAction, scheduleNextAction]);

  return <div>
    <div className={css.automationTestContainer}>
      <h2>WebRTC Softphone Test</h2>
      <div className={css.status}>Endpoint Status: {endpoint.status}</div>
      {endpoint.statusError && <div className={css.error}>{endpoint.statusError}</div>}
      {line.statusError && <div className={css.error}>{line.statusError}</div>}
      <div className={css.status}>Line Status: {line.status}</div>
      <label>Auto Dial<input type='number' value={autoDialNumber} onChange={e => setAutoDialNumber(e.currentTarget.value)} /></label>
      <Toggle className={css.toggle} value={autoAnswer} onChange={(value) => setAutoAnswer(value)}>Auto Answer Calls</Toggle>
      <Toggle className={css.toggle} value={allowHangUp} onChange={(value) => setAllowHangUp(value)}>Allow Hang Up</Toggle>
      <Toggle className={css.toggle} value={allowHoldResume} onChange={(value) => setAllowHoldResume(value)}>Allow Hold/Resume</Toggle>
      <Toggle className={css.toggle} value={allowIdle} onChange={(value) => setAllowIdle(value)}>Allow Idle</Toggle>
      <label>Min Delay<input type='number' value={minDelayMs} onChange={e => setMinDelayMs(e.currentTarget.valueAsNumber)} min={1000} /></label>
      <label>Max Delay<input type='number' value={maxDelayMs} onChange={e => setMaxDelayMs(e.currentTarget.valueAsNumber)} min={1000} /></label>
      <div className={css.status}>Allowable Actions:</div>
      {allowableActions && <ul className={css.allowActionsList}>{allowableActions.map(action => (<li>{action}</li>))}</ul>}
      <div className={css.status}>Next Action: {nextAction == null ? 'None' : `${nextAction.Action} in ${nextActionRemainingTime}ms`} </div>

      <div className={css.startStopContainer}>
        <button disabled={isRunning} onClick={() => setIsRunning(true)}>Start</button>
        <button disabled={!isRunning} onClick={() => setIsRunning(false)}>Stop</button>
      </div>
    </div>
  </div>
}
